import React, { useContext, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ToggleButton from '@mui/material/ToggleButton';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';

import SearchIcon from '@mui/icons-material/Search';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import * as R from 'ramda';

import { useNavigate } from 'react-router-dom';

import { format, isValid, parseISO } from 'date-fns';
import { func, shape, string } from 'prop-types';
import { useGetAffiliateOptions, useUserRolePermissions } from 'hooks';
import { isNotEmpty } from 'utils/helpers/formValidations';

import { AuthContext } from 'authentication/AuthContext';
import { renderInput } from '../utils/autocompleteUtils';
import { useGetFilterValuesQuery } from '../reconsignmentsEndpoints';

const Filters = props => {
  const lgUp = useMediaQuery(theme => theme.breakpoints.up('lg'), { noSsr: true });
  const {
    definedValues,
    filters,
    getParams,
    initialState,
    persistFilters,
    setFilters,
    setIsFiltersOpen,
    setPageNumber,
    tab,
    triggerAuto,
    triggerManual,
  } = props;
  const navigate = useNavigate();
  const [dateErrors, setDateErrors] = useState({});
  const [localStartDate, setLocalStartDate] = useState(null);
  const [localEndDate, setLocalEndDate] = useState(null);

  const { affiliates, setCurrentSelectedAffiliate } = useContext(AuthContext);

  const { currentAffiliate } = useUserRolePermissions();
  const affiliateOptions = useGetAffiliateOptions(['supplier']);

  const params = {
    inventoryOwner: {
      companyCustomerIdName: currentAffiliate?.customerIdName,
      dataCompanyId: currentAffiliate?.dataCompanyId,
    },
  };

  const reset = () => {
    setFilters({ ...initialState });
    setPageNumber(0);
    persistFilters()();
    setDateErrors({});
  };

  const { data: filterValuesQuery = [], isFetching } = useGetFilterValuesQuery({ params });

  const { fromLocations = [], origins = [], products = [], toLocations = [] } = filterValuesQuery;

  const handleAffiliateChange = e => {
    const split = e.target.value.split('-');
    const affiliateIndex = affiliates.findIndex(
      ({ customerIdName, dataCompanyId: id }) =>
        split[0] === customerIdName && parseInt(split[1], 10) === id
    );
    const affiliate = affiliates[affiliateIndex];
    reset();
    setCurrentSelectedAffiliate(affiliateIndex);
    navigate(
      `/affiliate/${affiliate.customerIdName}/${affiliate.dataCompanyId}/inventory-management/reconsignments`,
      {
        replace: true,
      }
    );
  };

  const {
    transferEndDate,
    transferStartDate,
    fromLocation,
    origin,
    product,
    reconsignmentId,
    status,
    toLocation,
  } = filters;
  useEffect(() => {
    const calculatedStartDate =
      transferStartDate && isValid(parseISO(transferStartDate))
        ? parseISO(transferStartDate)
        : new Date();
    setLocalStartDate(calculatedStartDate);
  }, [transferStartDate]);

  useEffect(() => {
    const calculatedEndDate =
      transferEndDate && isValid(parseISO(transferEndDate))
        ? parseISO(transferEndDate)
        : new Date();
    setLocalEndDate(calculatedEndDate);
  }, [transferEndDate]);

  const startDate =
    localStartDate ??
    (transferStartDate && isValid(parseISO(transferStartDate))
      ? parseISO(transferStartDate)
      : new Date());

  const endDate =
    localEndDate ??
    (transferEndDate && isValid(parseISO(transferEndDate))
      ? parseISO(transferEndDate)
      : new Date());
  const handleTransferStartDateChange = date => {
    const newDate = date && isValid(date) ? date : new Date();
    const formattedDate = format(newDate, 'yyyy-MM-dd');
    setLocalStartDate(newDate);
    // Update filter state
    R.compose(setFilters, R.assoc('transferStartDate'), R.always(formattedDate))();
  };

  const handleTransferEndDateChange = date => {
    const newDate = date && isValid(date) ? date : new Date();
    const formattedDate = format(newDate, 'yyyy-MM-dd');
    setLocalEndDate(newDate);
    // Update filter state
    R.compose(setFilters, R.assoc('transferEndDate'), R.always(formattedDate))();
  };
  const [validation, setValidation] = useState('');

  const isOnlyInt = val => R.not(/^[0-9]*$/.test(val));

  const validateReconsignmentId = R.cond([
    [isOnlyInt, R.always('Must only be numbers')],
    [R.T, R.always('')],
  ]);

  const handleReconsignmentIdChange = R.compose(
    setFilters,
    R.assoc('reconsignmentId'),
    R.tap(R.compose(setValidation, validateReconsignmentId)),
    R.path(['target', 'value'])
  );

  return (
    <>
      <Stack
        direction="row"
        sx={{ alignItems: 'center', justifyContent: 'space-between', padding: 2 }}
      >
        <Typography>Filters</Typography>
        <Button onClick={reset} variant="text">
          Clear Filters
        </Button>
      </Stack>
      <Stack spacing={2} sx={{ padding: theme => theme.spacing(3, 2, 10) }}>
        <FormControl className="css-heap-redact" sx={{ flex: 1 }}>
          <InputLabel id="authorization-type">Affiliate</InputLabel>
          <Select
            label="Affiliate"
            labelId="affiliate"
            onChange={handleAffiliateChange}
            value={`${currentAffiliate?.customerIdName}-${currentAffiliate?.dataCompanyId}`}
          >
            {affiliateOptions.map(({ label, value }) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          error={isNotEmpty(validation)}
          helperText={validation}
          label="Reconsignment ID"
          onChange={handleReconsignmentIdChange}
          slotProps={{
            input: {
              endAdornment: <SearchIcon color="inherit" size={20} />,
            },
          }}
          value={reconsignmentId}
        />
        <Divider />
        {R.equals('manual', tab) && (
          <ToggleButtonGroup
            exclusive
            fullWidth
            onChange={(_, value) =>
              value && R.compose(setFilters, R.assoc('status'), R.always(value))()
            }
            value={status}
          >
            <ToggleButton value="ALL">ALL</ToggleButton>
            <ToggleButton value="ERROR">ERROR</ToggleButton>
            <ToggleButton value="CLOSED">CLOSED</ToggleButton>
          </ToggleButtonGroup>
        )}
        <Stack
          direction={lgUp ? 'row' : 'column'}
          spacing={lgUp ? 1 : 2}
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <DatePicker
            disabled={R.equals('auto', tab)}
            label="Transfer Start"
            maxDate={endDate ?? undefined}
            onChange={handleTransferStartDateChange}
            onError={reason => setDateErrors(prev => ({ ...prev, start: reason }))}
            openTo="day"
            slotProps={{
              textField: {
                fullWidth: true,
                onBlur: event => {
                  if (!event.target.value || event.target.value === 'MM/DD/YYYY') {
                    handleTransferStartDateChange(null);
                  }
                },
                sx: { flex: 1 },
              },
            }}
            value={startDate}
            views={['year', 'month', 'day']}
          />
          <DatePicker
            disabled={R.equals('auto', tab)}
            label="Transfer End"
            minDate={startDate ?? undefined}
            onChange={handleTransferEndDateChange}
            onError={reason => setDateErrors(prev => ({ ...prev, end: reason }))}
            openTo="day"
            slotProps={{
              textField: {
                fullWidth: true,
                onBlur: event => {
                  if (!event.target.value || event.target.value === 'MM/DD/YYYY') {
                    handleTransferEndDateChange(null);
                  }
                },
                sx: { flex: 1 },
              },
            }}
            value={endDate}
            views={['year', 'month', 'day']}
          />
        </Stack>
        <Autocomplete
          className="css-heap-redact"
          getOptionLabel={option => R.defaultTo('', R.prop('displayName', option))}
          isOptionEqualToValue={R.eqProps('idName')}
          loading={isFetching}
          onChange={(_, value) => {
            R.compose(setFilters, R.assoc('product'), R.always(value))();
          }}
          options={products}
          renderInput={renderInput({
            label: 'Product',
            loading: isFetching,
            value: product,
          })}
          value={product}
        />
        <Autocomplete
          getOptionLabel={option => R.defaultTo('', R.prop('displayName', option))}
          isOptionEqualToValue={R.eqProps('idName')}
          loading={isFetching}
          onChange={(_, value) => {
            R.compose(setFilters, R.assoc('origin'), R.always(value))();
          }}
          options={origins}
          renderInput={renderInput({
            label: 'Origin',
            loading: isFetching,
            value: origin,
          })}
          value={origin}
        />
        <Autocomplete
          fullWidth
          getOptionLabel={option => R.defaultTo('', R.prop('displayName', option))}
          isOptionEqualToValue={R.eqProps('idName')}
          loading={isFetching}
          onChange={(_, value) => {
            R.compose(setFilters, R.assoc('fromLocation'), R.always(value))();
          }}
          options={fromLocations}
          renderInput={renderInput({
            label: 'Location (From)',
            loading: isFetching,
            value: fromLocation,
          })}
          value={fromLocation}
        />
        <Autocomplete
          fullWidth
          getOptionLabel={option => R.defaultTo('', R.prop('displayName', option))}
          isOptionEqualToValue={R.eqProps('idName')}
          loading={isFetching}
          onChange={(_, value) => {
            R.compose(setFilters, R.assoc('toLocation'), R.always(value))();
          }}
          options={toLocations}
          renderInput={renderInput({
            label: 'Location (To)',
            loading: isFetching,
            value: toLocation,
          })}
          value={toLocation}
        />
      </Stack>
      <Box
        sx={{
          backgroundColor: 'surface.white',
          bottom: 0,
          boxShadow: 0,
          position: 'fixed',
          py: '18px',
          textAlign: 'center',
          // eslint-disable-next-line sort-keys-fix/sort-keys-fix
          width: { xs: '100%', lg: 380 },
          zIndex: 1,
        }}
      >
        <Button
          className="css-heap-redact"
          disabled={Boolean(dateErrors.start) || Boolean(dateErrors.end)}
          onClick={persistFilters(() => {
            setPageNumber(0);
            triggerManual({ params: { ...getParams, ...definedValues } });
            triggerAuto({ params: { ...getParams, ...definedValues } });
            setIsFiltersOpen(false);
          })}
          sx={{ display: 'unset', position: 'unset', width: 159 }}
        >
          Search
        </Button>
      </Box>
    </>
  );
};

Filters.propTypes = {
  definedValues: shape({}).isRequired,
  filters: shape({}).isRequired,
  getParams: shape({}).isRequired,
  initialState: shape({}).isRequired,
  persistFilters: func.isRequired,
  setFilters: func.isRequired,
  setIsFiltersOpen: func.isRequired,
  setPageNumber: func.isRequired,
  tab: string.isRequired,
  triggerAuto: func.isRequired,
  triggerManual: func.isRequired,
};

export default Filters;
